import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import api from '@/services/apiClient';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import { GOAL_LABELS, fmtDate, fmtNum } from '@/lib/enums';
import ExerciseProgressSection from './ExerciseProgressSection';
import AssessmentsSection from './AssessmentsSection';
import FitnessTestsSection from './FitnessTestsSection';

const TABS = ['summary', 'assessments', 'tests'];

// Aluno convidado que ainda não definiu senha: link de convite reenviável.
function InviteBanner({ student }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const resend = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/students/${student.id}/reinvite`);
      setUrl((res.data || res).inviteUrl);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{ background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--warning)', fontSize: 13 }}>{t('students.invite.pending')}</span>
      {url ? (
        <>
          <code style={{ fontSize: 11, color: 'var(--mid)', wordBreak: 'break-all', flex: 1 }}>{url}</code>
          <button type="button" style={{ background: 'none', border: 'none', color: 'var(--volt)', cursor: 'pointer', fontSize: 12 }} onClick={() => navigator.clipboard?.writeText(url)}>
            {t('students.invite.copy')}
          </button>
        </>
      ) : (
        <button type="button" disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--volt)', cursor: 'pointer', fontSize: 13 }} onClick={resend}>
          {t('students.invite.resend')}
        </button>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--low)' }}>{label}</div>
      <div style={{ color: 'var(--hi)', fontSize: 15, marginTop: 3 }}>{value || '—'}</div>
    </div>
  );
}

function MetricCard({ label, value, delta, tone = 'hi' }) {
  const color = tone === 'accent' ? 'var(--volt)' : tone === 'recovery' ? 'var(--frost)' : 'var(--hi)';
  return (
    <div style={{ flex: 1, background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: 15 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--low)' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 24, color }}>{value}</span>
        {delta && <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--volt)' }}>{delta}</span>}
      </div>
    </div>
  );
}

export default function StudentDetail({ student }) {
  const { t } = useTranslation();
  const profile = student.studentProfile || student;
  const userId = student.userId || student.user?.id || student.id;
  const [tab, setTab] = useState('summary');
  const [measurements, setMeasurements] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api
      .get('/evolution/measurements', { userId })
      .then((res) => setMeasurements((res.data || res).measurements || res.data || []))
      .catch(() => setMeasurements([]));
    api
      .get('/evolution/summary', { userId })
      .then((res) => setSummary(res.data || res))
      .catch(() => setSummary(null));
  }, [student]);

  // A API devolve as medidas em ordem cronológica (ASC) — plota direto.
  const chart = (measurements || []).map((m) => ({
    d: fmtDate(m.date || m.createdAt).slice(0, 5),
    peso: m.weightKg ?? m.weight,
    gordura: m.bodyFatPct ?? m.bodyFat,
  }));

  const tabBtn = (key) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      style={{
        background: tab === key ? 'var(--surf2)' : 'transparent',
        border: `1px solid ${tab === key ? 'var(--volt)' : 'var(--line)'}`,
        borderRadius: 999,
        padding: '6px 14px',
        color: tab === key ? 'var(--hi)' : 'var(--mid)',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
    >
      {t(`students.tabs.${key}`)}
    </button>
  );

  if (tab !== 'summary') {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', gap: 8 }}>{TABS.map(tabBtn)}</div>
        {tab === 'assessments' ? (
          <AssessmentsSection userId={userId} profile={profile} />
        ) : (
          <FitnessTestsSection userId={userId} />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <div style={{ display: 'flex', gap: 8 }}>{TABS.map(tabBtn)}</div>

      {student.hasPassword === false && <InviteBanner student={student} />}

      <div style={{ display: 'flex', gap: 14 }}>
        <MetricCard
          label={t('students.detail.currentWeight')}
          value={summary?.latestWeight == null ? '—' : `${fmtNum(summary.latestWeight)}kg`}
          tone="accent"
        />
        <MetricCard
          label={t('students.detail.weightChange')}
          value={summary?.weightDelta == null ? '—' : `${summary.weightDelta > 0 ? '+' : ''}${fmtNum(summary.weightDelta)}kg`}
          tone="recovery"
        />
        <MetricCard
          label={t('students.detail.records')}
          value={fmtNum(summary?.measurementCount ?? (measurements?.length ?? 0))}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)' }}>
        <Field label={t('students.detail.email')} value={student.email} />
        <Field label={t('students.detail.phone')} value={student.phone} />
        <Field label={t('students.detail.goal')} value={profile.goal ? <Badge variant="accent">{GOAL_LABELS[profile.goal] || profile.goal}</Badge> : null} />
        <Field label={t('students.detail.height')} value={profile.heightCm ? `${profile.heightCm} cm` : null} />
        <Field label={t('students.detail.sex')} value={profile.sex ? t(`students.form.sexes.${profile.sex}`) : null} />
        <Field label={t('students.detail.weeklyFrequency')} value={profile.weeklyFrequency ? `${profile.weeklyFrequency}x` : null} />
        <Field label={t('students.detail.birthdate')} value={profile.birthdate ? fmtDate(profile.birthdate) : null} />
      </div>
      {profile.observations && <Field label={t('students.detail.observations')} value={profile.observations} />}

      <div>
        <div className="kicker" style={{ marginBottom: 12 }}>{t('students.detail.measurements')}</div>
        {measurements === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}><Spinner /></div>
        ) : chart.length === 0 ? (
          <EmptyState title={t('students.detail.noMeasurements')} description={t('students.detail.noMeasurementsDesc')} />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="d" stroke="var(--low)" fontSize={11} />
              <YAxis stroke="var(--low)" fontSize={11} />
              <Tooltip contentStyle={{ background: 'var(--surf2)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--hi)' }} />
              <Line type="monotone" dataKey="peso" stroke="var(--volt)" strokeWidth={2} dot={false} name={t('students.detail.weightLine')} />
              <Line type="monotone" dataKey="gordura" stroke="var(--frost)" strokeWidth={2} dot={false} name={t('students.detail.bodyFatLine')} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <ExerciseProgressSection userId={userId} MetricCard={MetricCard} />
    </div>
  );
}
